import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, ShoppingCart, AlertTriangle, Package, 
  ArrowRight, DollarSign, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const [salesSummary, setSalesSummary] = useState({ daily: {}, weekly: {} });
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, lowStockRes, bestSellersRes] = await Promise.all([
        axios.get(`${API}/reports/sales-summary`),
        axios.get(`${API}/products/low-stock/list`),
        axios.get(`${API}/reports/best-sellers?limit=5`)
      ]);
      
      setSalesSummary(summaryRes.data);
      setLowStockProducts(lowStockRes.data);
      setBestSellers(bestSellersRes.data);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1 font-body">Overview of your café operations</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Today's Revenue</CardTitle>
            <DollarSign className="w-5 h-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="price-display text-3xl text-slate-900">
              £{(salesSummary.daily?.revenue || 0).toFixed(2)}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {salesSummary.daily?.transactions || 0} transactions
            </p>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">This Week</CardTitle>
            <Calendar className="w-5 h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="price-display text-3xl text-slate-900">
              £{(salesSummary.weekly?.revenue || 0).toFixed(2)}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {salesSummary.weekly?.transactions || 0} transactions
            </p>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Low Stock Items</CardTitle>
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="price-display text-3xl text-slate-900">
              {lowStockProducts.length}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              items need restocking
            </p>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Top Seller</CardTitle>
            <TrendingUp className="w-5 h-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-xl font-bold text-slate-900 truncate">
              {bestSellers[0]?.product_name || 'N/A'}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {bestSellers[0]?.total_quantity || 0} units sold
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              Low Stock Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {lowStockProducts.slice(0, 6).map((product) => (
                <div
                  key={product.id}
                  data-testid={`low-stock-${product.id}`}
                  className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-200"
                >
                  <span className="font-medium text-slate-700">{product.name}</span>
                  <span className="low-stock-badge">{product.stock_qty} left</span>
                </div>
              ))}
            </div>
            {lowStockProducts.length > 6 && (
              <Button
                variant="link"
                onClick={() => navigate('/admin/restock')}
                className="mt-4 text-amber-700"
              >
                View all {lowStockProducts.length} items
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Best Sellers */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl">Best Sellers</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/reports')}
          >
            View Reports
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {bestSellers.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No sales data yet</p>
          ) : (
            <div className="space-y-3">
              {bestSellers.map((item, idx) => (
                <div
                  key={item.product_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-slate-200 rounded-full font-bold text-slate-600">
                      {idx + 1}
                    </span>
                    <span className="font-medium text-slate-700">{item.product_name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-bold text-slate-900">
                      {item.total_quantity} sold
                    </p>
                    <p className="text-sm text-slate-500">
                      £{item.total_revenue.toFixed(2)} revenue
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
